import { useState, type ReactNode } from "react";
import type { AccountInfo } from "@azure/msal-browser";
import { createControlClient, idleHierarchy, idleRecordingPages, type ControlClient } from "../api/client.ts";
import type { Device } from "../api/parse.ts";
import { formatInstallerSaveName } from "../api/installerSaveName.ts";
import { ControlError } from "../api/parse.ts";
import type { OperatorAuth } from "../auth.ts";
import { consentCardClient, consentWaitClient } from "../desk/consentCardFixture.ts";
import { connectFixtureClient } from "../desk/connectFixture.ts";
import { missFixtureClient } from "../desk/missFixture.ts";
import type { PortalConfig, PortalEnvironment } from "../config.ts";
import { FIXTURE_ADMIN_ACCESS, FIXTURE_NO_ACCESS, FIXTURE_OPERATOR_ACCESS, readsAudit, NO_ACCESS_MESSAGE, type MenuAccess } from "./access.ts";
import { API_VERSION_STUB, PORTAL_VERSION, semverOrStub } from "./shipStamp.ts";
import { ChromeNavIcon, type ChromeNavIconKind } from "./iconAction.tsx";

export { API_VERSION_STUB, PORTAL_VERSION, semverOrStub };
export const WYLO_MARK_PATH = "/brand/wylo/wylo-mark.jpeg";
export const WYLO_MARK_TEST_PATH = "/brand/wylo/wylo-mark-test.png";

export function wyloMarkPath(environment: PortalEnvironment): string {
  return environment === "prod" ? WYLO_MARK_PATH : WYLO_MARK_TEST_PATH;
}

export function documentMarkHref(environment: PortalEnvironment, version: string): string {
  return `${wyloMarkPath(environment)}?v=${version}`;
}

function documentMarkLink(rel: string): HTMLLinkElement {
  const found = document.head.querySelector(`link[rel="${rel}"]`);
  if (found instanceof HTMLLinkElement) return found;
  const created = document.createElement("link");
  created.rel = rel;
  document.head.appendChild(created);
  return created;
}

export function applyDocumentMark(environment: PortalEnvironment): void {
  const href = documentMarkHref(environment, PORTAL_VERSION);
  const type = environment === "prod" ? "image/jpeg" : "image/png";
  for (const rel of ["icon", "apple-touch-icon"]) {
    const link = documentMarkLink(rel);
    link.setAttribute("type", type);
    link.setAttribute("href", href);
    if (rel === "icon") link.setAttribute("sizes", "any");
  }
}

export function WyloWatermark(props: { anchored?: boolean; environment?: PortalEnvironment }) {
  return (
    <a
      className={props.anchored ? "watermark stage-mark" : "watermark"}
      href="https://wylosolutions.com"
      target="_blank"
      rel="noopener noreferrer"
      onPointerDown={(event) => {
        if (props.anchored) event.stopPropagation();
      }}
    >
      <span>Powered by</span>
      <img src={WYLO_MARK_PATH} alt="" />
      <span>Managed IT</span>
      <span>Services</span>
    </a>
  );
}

export function accountLabel(account: { name?: string | null; username: string }): { name: string; email: string } {
  const email = account.username.trim();
  const given = account.name?.trim() ?? "";
  return { name: given.length > 0 ? given : email, email };
}

export function accountInitials(name: string): string {
  const email = name.includes("@");
  const source = email ? (name.split("@")[0] ?? name) : name;
  const parts = source.split(email ? /[._-]+/ : /\s+/).filter((part) => part.length > 0);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]!.slice(0, 1).toUpperCase();
  return `${parts[0]![0] ?? ""}${parts[parts.length - 1]![0] ?? ""}`.toUpperCase();
}

export function envChromeLabel(environment: PortalEnvironment): string | null {
  if (environment === "prod") return null;
  return environment.toUpperCase();
}

function chromeHref(target: string): string {
  const verify = import.meta.env.DEV ? new URLSearchParams(window.location.search).get("wyloVerify") : null;
  if (!verify) return target;
  return `${target}?wyloVerify=${encodeURIComponent(verify)}`;
}

function ChromeNavLink(props: { href: string; current: boolean; icon: ChromeNavIconKind; label: string }) {
  return (
    <a href={props.href} aria-current={props.current ? "page" : undefined}>
      <ChromeNavIcon kind={props.icon} />
      {props.label}
    </a>
  );
}

function ChromeLinks(props: { access?: MenuAccess | null }) {
  const path = window.location.pathname;
  const trimmed = path.length > 1 && path.endsWith("/") ? path.slice(0, -1) : path;
  const audit = trimmed === "/admin/audit" || trimmed === "/audit";
  const settings =
    !audit &&
    (trimmed === "/admin" ||
      trimmed.startsWith("/admin/") ||
      trimmed === "/settings" ||
      trimmed.startsWith("/settings/"));
  const recordings = trimmed === "/recordings";
  const devices = !audit && !settings && !recordings;
  return (
    <nav className="chrome-links" aria-label="Portal">
      <ChromeNavLink href={chromeHref("/")} current={devices} icon="devices" label="Devices" />
      <ChromeNavLink href={chromeHref("/recordings")} current={recordings} icon="recordings" label="Recordings" />
      {readsAudit(props.access) ? (
        <ChromeNavLink href={chromeHref("/audit")} current={audit} icon="audit" label="Audit log" />
      ) : null}
      {props.access?.administer ? (
        <ChromeNavLink href={chromeHref("/settings")} current={settings} icon="settings" label="Settings" />
      ) : null}
    </nav>
  );
}

export function PortalChrome(props: {
  account: AccountInfo | null;
  apiBase: string;
  environment: PortalEnvironment;
  access?: MenuAccess | null;
  adminHref?: string;
  fill?: boolean;
  onSignOut: () => void;
  children: ReactNode;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [canConnectOpen, setCanConnectOpen] = useState(false);
  const label = props.account ? accountLabel(props.account) : null;
  const mark = wyloMarkPath(props.environment);
  const envLabel = envChromeLabel(props.environment);
  const roleLabel = props.access?.roleLabel ?? "None";
  const canConnect = props.access?.canConnect ?? "None";
  const noRoles = props.access !== undefined && props.access !== null && props.access.roles.length === 0;

  const frameClass = props.fill ? "app-frame session-frame" : "app-frame";
  const shellClass = props.fill ? "session-shell" : "shell";
  return (
    <div className={frameClass}>
      <header className="chrome-bar">
        <div className="chrome-brand">
          <img src={mark} alt="WYLO" />
          <h1>Wylo Remote Connect</h1>
        </div>
        {envLabel !== null ? <span className="chrome-env">{envLabel}</span> : null}
        {label ? (
          <div className="chrome-account-wrap">
            {noRoles ? null : <ChromeLinks access={props.access} />}
            <button
              type="button"
              className="chrome-account"
              aria-label={label.name}
              aria-expanded={menuOpen}
              aria-controls="account-menu"
              onClick={() => setMenuOpen((open) => !open)}
            >
              {accountInitials(label.name)}
            </button>
            {menuOpen ? (
              <div id="account-menu" className="account-menu" role="menu">
                <p>{label.email}</p>
                <p>{roleLabel}</p>
                <p className="can-connect-row">
                  <button
                    type="button"
                    className="can-connect"
                    aria-expanded={canConnectOpen}
                    aria-label="Can connect"
                    onClick={() => setCanConnectOpen((open) => !open)}
                  >
                    +
                  </button>
                  <span>Can connect</span>
                </p>
                {canConnectOpen ? <p>{canConnect}</p> : null}
                <button type="button" className="sign-out" onClick={props.onSignOut}>
                  Sign out
                </button>
              </div>
            ) : null}
          </div>
        ) : null}
      </header>
      <main className={shellClass}>{noRoles ? <p className="panel">{NO_ACCESS_MESSAGE}</p> : props.children}</main>
      {props.fill ? null : <WyloWatermark environment={props.environment} />}
    </div>
  );
}

const FIXTURE_NAME = "Avery Quinn";
const FIXTURE_EMAIL = "avery.quinn@wylo.example";

export function devChromeSession(
  config: PortalConfig,
): { auth: OperatorAuth; client: ControlClient; access: MenuAccess } | null {
  if (!import.meta.env.DEV) return null;
  const flag = new URLSearchParams(window.location.search).get("wyloVerify");
  if (
    flag !== "chrome" &&
    flag !== "inventory" &&
    flag !== "session" &&
    flag !== "devices" &&
    flag !== "admin-devices" &&
    flag !== "overview" &&
    flag !== "roles" &&
    flag !== "miss" &&
    flag !== "connect" &&
    flag !== "input" &&
    flag !== "consent-timeout" &&
    flag !== "consent-wait" &&
    flag !== "admin" &&
    flag !== "no-access" &&
    flag !== "users-status" &&
    flag !== "operator-role" &&
    flag !== "recordings" &&
    flag !== "audit" &&
    flag !== "recording"
  ) {
    return null;
  }
  const access = flag === "operator-role" ? FIXTURE_OPERATOR_ACCESS : flag === "no-access" ? FIXTURE_NO_ACCESS : FIXTURE_ADMIN_ACCESS;
  const eric =
    flag === "devices" ||
    flag === "admin-devices" ||
    flag === "overview" ||
    flag === "roles" ||
    flag === "admin" ||
    flag === "users-status" ||
    flag === "recordings" ||
    flag === "audit" ||
    flag === "recording";
  const account = {
    homeAccountId: "verify-home",
    environment: "login.microsoftonline.com",
    tenantId: config.tenantId,
    username: eric ? "tst_eric@wylosolutions.com" : FIXTURE_EMAIL,
    localAccountId: "verify-local",
    name: eric ? "Eric" : FIXTURE_NAME,
    idTokenClaims: { oid: "00000000-0000-4000-8000-0000000000bb" },
  };
  async function signOut() {
    const url = new URL(window.location.href);
    url.searchParams.delete("wyloVerify");
    window.location.assign(`${url.pathname}${url.search}`);
  }
  if (flag === "recordings" || flag === "audit") {
    const params = new URLSearchParams(window.location.search);
    const sample = params.get("sample") === "1";
    const fail = params.get("fail") === "1";
    const problem = new ControlError({
      type: "urn:wylo-rc:error:forbidden",
      title: "Forbidden",
      status: 403,
      detail: "Recording catalogue is not available.",
    });
    return {
      access,
      auth: {
        account,
        signIn: async () => undefined,
        signOut,
        token: async () => "verify-recordings",
      },
      client: {
        ...idleRecordingPages(),
        ...idleHierarchy(),
        listDevices: async () => ({ items: []}),
        approveDevice: async () => {
          throw new Error("verify recordings fixture");
        },
        renameDevice: async () => {
          throw new Error("verify recordings fixture");
        },
        listOrganisations: async () => [],
        listDepartments: async () => [],
        createSession: async () => {
          throw new Error("verify recordings fixture");
        },
        getSession: async () => {
          throw new Error("verify recordings fixture");
        },
        endSession: async () => {
          throw new Error("verify recordings fixture");
        },
        listRecordings: async () => [],
        listCatalogue: async () => {
          if (fail) throw problem;
          if (!sample) return { items: [] };
          return {
            items: [
              {
                recordingId: "00000000-0000-4000-8000-0000000000c1",
                sessionId: "00000000-0000-4000-8000-0000000000c2",
                deviceId: "00000000-0000-4000-8000-0000000000c3",
                contentType: "video/webm",
                sha256: "8f434346648f6b96df89dda901c5176b10a6d83961dd3c1ac88b59b2dc327aa4",
                bytes: 1536,
                retentionUntil: "2026-12-23T00:00:00.000Z",
                createdAt: "2026-10-06T21:00:00.000Z",
                deviceName: "till-01",
                connectedUser: "casey.quinn",
                startedAt: "2026-10-23T20:48:00.000Z",
                endedAt: "2026-10-23T21:01:00.000Z",
              },
            ],
          };
        },
        listRecordingAccess: async () => {
          if (fail) throw problem;
          if (!sample) return { items: [], nextCursor: null };
          return {
            items: [
              {
                eventId: "00000000-0000-4000-8000-0000000000d1",
                occurredAt: "2026-10-06T21:05:00.000Z",
                who: "oid-operator",
                whoGuid: "oid-operator",
                whoSource: null,
                deviceId: "00000000-0000-4000-8000-0000000000c3",
                sessionId: "00000000-0000-4000-8000-0000000000c2",
                intent: "Play",
              },
            ],
            nextCursor: null,
          };
        },
        listAuditEvents: async () => {
          if (fail) throw problem;
          if (!sample) return { items: [], nextCursor: null };
          return {
            items: [
              {
                eventId: "00000000-0000-4000-8000-0000000000d1",
                type: "RecordingAccessed",
                occurredAt: "2026-10-06T21:05:00.000Z",
                who: "tst_eric@wylosolutions.com",
                whoGuid: "oid-operator",
                whoSource: null,
                deviceId: "00000000-0000-4000-8000-0000000000c3",
                sessionId: "00000000-0000-4000-8000-0000000000c2",
                detail: "Play",
              },
              {
                eventId: "00000000-0000-4000-8000-0000000000d2",
                type: "DeviceEnrolled",
                occurredAt: "2026-10-05T09:00:00.000Z",
                who: "sam.cole@wylo.example",
                whoGuid: "oid-sam",
                whoSource: null,
                deviceId: "00000000-0000-4000-8000-0000000000a1",
                sessionId: null,
                detail: "Enrolled",
              },
            ],
            nextCursor: null,
          };
        },
        recordingContent: async () => new Blob([new Uint8Array([0x1a, 0x45, 0xdf, 0xa3])], { type: "video/webm" }),
        reportSessionEvent: async () => undefined,
        requestAdminConsole: async () => ({ status: "queued" as const }),
        downloadAgentInstaller: async () => {
          throw new Error("verify recordings fixture");
        },
      },
    };
  }
  if (flag === "input") {
    const unused = async () => {
      throw new Error("verify input fixture");
    };
    return {
      access,
      auth: {
        account,
        signIn: async () => undefined,
        signOut,
        token: async () => "verify-input",
      },
      client: {
        listDevices: async () => ({ items: []}),
        approveDevice: unused,
        renameDevice: unused,
        listOrganisations: async () => [],
        listDepartments: async () => [],
        createSession: unused,
        getSession: unused,
        endSession: unused,
        ...idleRecordingPages(),
        listRecordings: async () => [],
        recordingContent: unused,
        reportSessionEvent: async () => undefined,
        requestAdminConsole: async () => ({ status: "queued" as const }),
        downloadAgentInstaller: unused,
      },
    };
  }
  if (flag === "consent-wait") {
    return {
      access,
      auth: {
        account,
        signIn: async () => undefined,
        signOut,
        token: async () => "verify-consent-wait",
      },
      client: consentWaitClient(new URLSearchParams(window.location.search).get("deadlineAt")),
    };
  }
  if (flag === "consent-timeout") {
    return {
      access,
      auth: {
        account,
        signIn: async () => undefined,
        signOut,
        token: async () => "verify-consent-timeout",
      },
      client: consentCardClient(),
    };
  }
  if (flag === "connect") {
    return {
      access,
      auth: {
        account,
        signIn: async () => undefined,
        signOut,
        token: async () => "verify-connect",
      },
      client: connectFixtureClient(),
    };
  }
  if (flag === "miss") {
    return {
      access,
      auth: {
        account,
        signIn: async () => undefined,
        signOut,
        token: async () => "verify-miss",
      },
      client: missFixtureClient(),
    };
  }
  if (flag === "devices" || flag === "admin-devices") {
    const orgId = "00000000-0000-4000-8000-0000000000b1";
    const deptId = "00000000-0000-4000-8000-0000000000b2";
    const partial = flag === "devices" && new URLSearchParams(window.location.search).get("partial") === "1";
    const items: Device[] = [
      {
        deviceId: "00000000-0000-4000-8000-0000000000a3",
        status: "Pending",
        presence: "Offline",
        hostname: "till-pending",
        name: "till-pending",
        agentVersion: "0.1.0",
        createdAt: "2026-10-06T21:00:00.000Z",
        lastBootAt: "2026-09-28T06:00:00Z",
        loggedOnUser: "sam",
        os: { platform: "windows", version: "Windows 11 Pro Version 24H2 (OS Build 26100.1742)", architecture: "x64" },
        displays: [],
        recordingSelectable: false,
        organisationId: orgId,
        departmentId: deptId,
        tags: [],
      },
      {
        deviceId: "00000000-0000-4000-8000-0000000000a1",
        status: "Enrolled",
        presence: "Online",
        hostname: "till-online",
        name: "till-online",
        agentVersion: "0.1.0",
        createdAt: "2026-10-06T21:00:00.000Z",
        lastHeartbeatAt: "2026-09-28T12:35:01Z",
        lastBootAt: "2026-09-28T08:00:00Z",
        loggedOnUser: "alex",
        organisationId: orgId,
        departmentId: deptId,
        os: { platform: "windows", version: "Windows 11 Pro Version 24H2 (OS Build 26100.1742)", architecture: "x64" },
        displays: [],
        recordingSelectable: true,
        stealthCommand: true,
        tags: [{ key: "site", value: "leeds" }],
      },
      {
        deviceId: "00000000-0000-4000-8000-0000000000a2",
        status: "Enrolled",
        presence: "Offline",
        hostname: "till-offline",
        name: "till-offline",
        agentVersion: "0.1.0",
        createdAt: "2026-10-06T21:00:00.000Z",
        organisationId: orgId,
        departmentId: deptId,
        os: { platform: "windows", version: "Windows 11 Pro Version 24H2 (OS Build 26100.1742)", architecture: "x64" },
        displays: [],
        recordingSelectable: false,
        tags: [
          { key: "site", value: "york" },
          { key: "kiosk", value: "" },
          { key: "floor", value: "2" },
          { key: "lane", value: "4" },
          { key: "zone", value: "front" },
          { key: "till", value: "self-serve" },
        ],
      },
    ];
    return {
      access,
      auth: {
        account,
        signIn: async () => undefined,
        signOut,
        token: async () => "verify-devices",
      },
      client: {
        listDevices: async (cursor) => {
          if (!partial) return { items };
          if (cursor) throw new Error("verify devices fixture");
          return { items, nextCursor: "older" };
        },
        approveDevice: async (deviceId) => {
          const device = items.find((item) => item.deviceId === deviceId);
          if (!device) throw new Error("verify devices fixture");
          device.status = "Enrolled";
          return device;
        },
        renameDevice: async (deviceId, name) => {
          const device = items.find((item) => item.deviceId === deviceId);
          if (!device) throw new Error("verify devices fixture");
          device.name = name;
          return device;
        },
        listOrganisations: async () => [{ organisationId: orgId, name: "Wylo Retail" }],
        listDepartments: async () => [{ departmentId: deptId, organisationId: orgId, name: "Checkout" }],
        createSession: async () => {
          throw new Error("verify devices fixture");
        },
        getSession: async () => {
          throw new Error("verify devices fixture");
        },
        endSession: async () => {
          throw new Error("verify devices fixture");
        },
        ...idleRecordingPages(),
        placeDevice: async (deviceId, organisationId, departmentId) => {
          const device = items.find((item) => item.deviceId === deviceId);
          if (!device) throw new Error("verify devices fixture");
          device.organisationId = organisationId;
          device.departmentId = departmentId;
          return device;
        },
        listRecordings: async () => [],
        recordingContent: async () => {
          throw new Error("verify devices fixture");
        },
        reportSessionEvent: async () => undefined,
        requestAdminConsole: async () => ({ status: "queued" as const }),
        downloadAgentInstaller: async () => ({
          fileName: formatInstallerSaveName("test", "0.1.0"),
          bytes: new Blob([new Uint8Array([0])]),
        }),
      },
    };
  }
  if (flag === "session") {
    const failed = new ControlError({
      type: "urn:wylo-rc:error:error",
      title: "Unexpected error",
      status: 500,
      detail: "InvalidOperationException",
    });
    const unused = async () => {
      throw failed;
    };
    return {
      access,
      auth: {
        account,
        signIn: async () => undefined,
        signOut,
        token: async () => "verify-session",
      },
      client: {
        listDevices: async () => ({
          items: [
            {
              deviceId: "00000000-0000-4000-8000-000000000011",
              status: "Enrolled",
              presence: "Online",
              hostname: "desk",
              name: "desk",
              agentVersion: "0.1.0",
              createdAt: "2026-10-06T21:00:00.000Z",
              os: { platform: "windows", version: "11", architecture: "x64" },
              displays: [],
        recordingSelectable: false,
              tags: [],
            },
            {
              deviceId: "00000000-0000-4000-8000-000000000012",
              status: "Enrolled",
              presence: "Online",
              hostname: "desk-two",
              name: "desk-two",
              agentVersion: "0.1.0",
              createdAt: "2026-10-06T21:00:00.000Z",
              os: { platform: "windows", version: "11", architecture: "x64" },
              displays: [],
        recordingSelectable: false,
              tags: [],
            },
          ],
        }),
        approveDevice: unused,
        renameDevice: unused,
        listOrganisations: async () => [],
        listDepartments: async () => [],
        createSession: async () => {
          throw failed;
        },
        getSession: unused,
        endSession: unused,
        ...idleRecordingPages(),
        listRecordings: async () => [],
        recordingContent: unused,
        reportSessionEvent: async () => undefined,
        requestAdminConsole: async () => ({ status: "queued" as const }),
        downloadAgentInstaller: async () => {
          throw new Error("Download agent is not available in this fixture.");
        },
      },
    };
  }
  if (flag === "inventory") {
    return {
      access,
      auth: {
        account,
        signIn: async () => undefined,
        signOut,
        token: async () => "verify-inventory",
      },
      client: createControlClient({
        apiBase: config.apiBase,
        token: async () => "verify-inventory",
      }),
    };
  }
  if (flag === "operator-role") {
    const unused = async () => {
      throw new Error("verify operator-role fixture");
    };
    return {
      access,
      auth: {
        account,
        signIn: async () => undefined,
        signOut,
        token: async () => "verify-operator-role",
      },
      client: {
        listDevices: async () => ({
          items: [
            {
              deviceId: "00000000-0000-4000-8000-000000000011",
              status: "Enrolled",
              presence: "Online",
              hostname: "desk",
              name: "desk",
              agentVersion: "0.1.0",
              createdAt: "2026-10-06T21:00:00.000Z",
              os: { platform: "windows", version: "11", architecture: "x64" },
              displays: [],
              recordingSelectable: false,
              tags: [],
            },
          ],
        }),
        approveDevice: unused,
        renameDevice: unused,
        listOrganisations: async () => [],
        listDepartments: async () => [],
        createSession: unused,
        getSession: unused,
        endSession: unused,
        ...idleRecordingPages(),
        listRecordings: async () => [],
        recordingContent: unused,
        reportSessionEvent: async () => undefined,
        requestAdminConsole: async () => ({ status: "queued" as const }),
        downloadAgentInstaller: async () => ({
          fileName: formatInstallerSaveName("test", "0.1.0"),
          bytes: new Blob([new Uint8Array([0])]),
        }),
      },
    };
  }
  const unused = async () => {
    throw new Error("verify chrome fixture");
  };
  return {
    access,
    auth: {
      account,
      signIn: async () => undefined,
      signOut,
      token: async () => "verify-chrome",
    },
    client: {
      listDevices: async () => ({ items: []}),
      approveDevice: unused,
      renameDevice: unused,
      listOrganisations: async () => [],
      listDepartments: async () => [],
      createSession: unused,
      getSession: unused,
      endSession: unused,
      ...idleRecordingPages(),
      ...(flag === "overview"
        ? {
            agentVersions: async () => ({
              windows: { published: true, version: "0.1.40" },
              macos: { published: false, version: null },
            }),
          }
        : {}),
      listRecordings: async () => [],
      recordingContent: unused,
      reportSessionEvent: async () => undefined,
      requestAdminConsole: async () => ({ status: "queued" as const }),
      downloadAgentInstaller:
        flag === "overview"
          ? async () => ({
              fileName: formatInstallerSaveName("test", "0.1.0"),
              bytes: new Blob([new Uint8Array([0])]),
            })
          : async () => {
              throw new Error("Download agent is not available in this fixture.");
            },
    },
  };
}
